import { Button, cn, toast, Typography } from "@suraasa/placebo-ui"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import api from "api"
import {
  AttachmentAIDetection,
  Submission,
} from "api/resources/submissions/types"
import { APIError } from "api/utils"

type Attachment = Submission["attachments"][number]

// Winston only reads these; hades rejects any other type
const CHECKABLE = [".pdf", ".doc", ".docx"]

// Signed file URLs don't always end in the extension, so the name counts too
const isCheckable = (a: Attachment) =>
  [a.name, a.file.split("?")[0]].some(path =>
    CHECKABLE.some(ext => path.toLowerCase().endsWith(ext))
  )

const POLL_INTERVAL = 12_000

/**
 * Reads the attachments' AI results hades already has, by file name.
 * Never starts a check: only `RunAttachmentAI` (professor portal) does that.
 */
export const useAttachmentAI = (
  submissionId: string | undefined,
  attachments: Attachment[] | undefined
) => {
  const { data } = useQuery({
    queryKey: ["attachments-ai", submissionId],
    queryFn: () =>
      api.submissions.listAttachments({
        urlParams: { submissionId: submissionId ?? null },
      }),
    enabled: !!submissionId && !!attachments?.some(isCheckable),
    // No push when a check finishes, so poll while one is running
    refetchInterval: query =>
      query.state.data?.some(a => a.aiCheckStatus === "in_progress")
        ? POLL_INTERVAL
        : false,
  })

  // Names are unique within a submission
  return new Map((data ?? []).map(a => [a.attachmentName, a]))
}

/** Professor portal only: starts the AI check for one attachment. */
export const RunAttachmentAI = ({
  submissionId,
  attachment,
  result,
}: {
  submissionId: string
  attachment: Attachment
  result: AttachmentAIDetection | undefined
}) => {
  const queryClient = useQueryClient()

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      api.submissions.registerAttachments({
        urlParams: { submissionId },
        data: {
          attachments: [
            {
              attachmentName: attachment.name,
              attachmentFilePath: attachment.file,
            },
          ],
        },
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["attachments-ai", submissionId],
      }),
    onError: e => {
      const status = e instanceof APIError ? e.statusCode : undefined
      toast.error(
        (e instanceof APIError && e.errors.message) ||
          (status === 404
            ? "Plagiarism checks aren't set up for this submission yet. Use the retry buttons above first."
            : "Something went wrong. Please try again later.")
      )
    },
  })

  const status = result?.aiCheckStatus
  if (
    !isCheckable(attachment) ||
    status === "in_progress" ||
    status === "completed"
  )
    return null

  return (
    <Button
      size="sm"
      variant="text"
      className="px-0.5 py-0.25"
      loading={isPending}
      onClick={() => mutate()}
    >
      {status === "failed" ? "Retry AI check" : "Check AI"}
    </Button>
  )
}

export const AttachmentAIBadge = ({
  result,
}: {
  result: AttachmentAIDetection | undefined
}) => {
  if (!result || result.aiCheckStatus === "not_started") return null

  const pct =
    result.aiCheckStatus === "completed" && result.aiDetectionPercentage
      ? Number(result.aiDetectionPercentage)
      : null

  if (pct === null)
    return (
      <Typography variant="smallBody" className="text-onSurface-500">
        {result.aiCheckStatus === "failed"
          ? "AI check failed"
          : "AI check running"}
      </Typography>
    )

  return (
    <span
      className={cn(
        "rounded border px-0.5 text-xs font-medium",
        pct <= 40 && "border-highlight-200 bg-highlight-50 text-highlight-900",
        pct > 40 &&
          pct <= 60 &&
          "border-warning-200 bg-warning-50 text-warning-900",
        pct > 60 && "border-critical-200 bg-critical-50 text-critical-900"
      )}
    >
      AI: {pct.toFixed(0)}%
    </span>
  )
}
